import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../Layout/Modal';
import ElevatedButton from '../Layout/ElevatedButton';
import CourseForm from './CourseForm';
import CourseDetailsModal from './CourseDetailsModal';
import CourseNotesModal from './CourseNotesModal';
import AssignmentList from '../Assignments/AssignmentList';
import SubscriptionModal from '../Layout/SubscriptionModal';
import { calculateCourseGPAProjection } from '../../utils/gpa';

const WarningText = styled.p`
  color: ${props => props.theme.warning};
  font-weight: 500;
  margin-bottom: 1rem;
`;

const GenericConfirmModal = ({ isOpen, onClose, title, message, confirmText = 'Confirm', cancelText = 'Cancel', onConfirm, confirmButtonColor = 'var(--color-error)' }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div>
        <p style={{ color: 'var(--color-text)', marginBottom: '1rem', fontSize: '1rem' }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--color-textMuted)',
              color: '#ffffff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#5a6268'}
            onMouseOut={(e) => e.target.style.background = 'var(--color-textMuted)'}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: confirmButtonColor,
              color: '#ffffff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = confirmButtonColor === 'var(--color-error)' ? '#c82333' : '#4a5568'}
            onMouseOut={(e) => e.target.style.background = confirmButtonColor}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CourseDeleteModal = ({ isOpen, onClose, courseName, onConfirm }) => {
  return (
    <GenericConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Course"
      message={
        <>
          Are you sure you want to delete <strong>"{courseName}"</strong>?
          <br />
          <WarningText style={{ marginTop: '1rem' }}>
            This will also delete ALL assignments associated with this course. This action cannot be undone.
          </WarningText>
        </>
      }
      confirmText="Delete Course"
      confirmButtonColor="#dc3545"
      onConfirm={onConfirm}
    />
  );
};

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, calc(25% - 0.75rem)));
  gap: 1rem;
  padding: 0;
  max-width: 100%;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, calc(33.333% - 0.67rem)));
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, calc(50% - 0.5rem)));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Item = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.25rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  color: ${props => props.theme.text};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const CourseCode = styled.span`
  font-weight: 600;
  color: ${props => props.color || props.theme.buttonPrimary};
  font-size: 1rem;
  font-family: 'Consolas', 'Monaco', monospace;
`;

const CourseDetails = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.theme.textMuted};
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CourseInfo = styled.div`
  flex: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EditButton = styled(ElevatedButton)`
  background: #2196f3;
  border: 1px solid #2196f3;

  &:hover {
    background: #1976d2;
    border: 1px solid #1976d2;
  }
`;

const CompleteButton = styled(ElevatedButton)`
  background: #28a745;
  border: 1px solid #28a745;

  &:hover {
    background: #218838;
    border: 1px solid #218838;
  }
`;

const DeleteButton = styled(ElevatedButton)`
  background: #dc3545;
  border: 1px solid #dc3545;

  &:hover {
    background: #c82333;
    border: 1px solid #c82333;
  }
`;

const UncompleteButton = styled(ElevatedButton)`
  background: #ff9800;
  border: 1px solid #ff9800;

  &:hover {
    background: #fb8c00;
    border: 1px solid #fb8c00;
  }
`;



const GradeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.grade) {
      case 'A': return '#28a745';
      case 'A-': return '#5cb85c';
      case 'B+': return '#8bc34a';
      case 'B': return '#c0ca33';
      case 'B-': return '#fbc02d';
      case 'C+': return '#ff9800';
      case 'C': return '#ff6f00';
      case 'C-': return '#ff5722';
      case 'D+': return '#f44336';
      case 'D': return '#e53935';
      case 'D-': return '#d32f2f';
      case 'F': return '#b71c1c';
      default: return '#6c757d';
    }
  }};
  color: white;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme.border};
  border-radius: 4px;
  overflow: hidden;
  margin: 0.5rem 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff4444 0%, #ffaa44 50%, #44aa44 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const ProgressText = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  text-align: center;
  margin-top: 0.25rem;
`;

const CourseList = ({ viewMode = 'active', onAddNew }) => {
  const { courses, deleteCourse, updateCourse, letterGradeToGPA, gpaToLetterGrade, settings, assignments } = useData();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isAllAssignmentsModalOpen, setIsAllAssignmentsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [courseToComplete, setCourseToComplete] = useState(null);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isUnmarking, setIsUnmarking] = useState(false);
  const [isCourseDetailsModalOpen, setIsCourseDetailsModalOpen] = useState(false);
  const [isCourseNotesModalOpen, setIsCourseNotesModalOpen] = useState(false);

  const isPremium = (user?.subscription && user?.subscription !== 'free') || (user?.aiTier && user?.aiTier !== 'free');
  const isCompact = isPremium && settings?.display?.compactMode;

  // Calculate course progress based on completed assignments
  const calculateCourseProgress = (courseId) => {
    const courseAssignments = assignments.filter(assignment => {
      // Handle both string IDs and populated course objects
      const assignmentCourseId = typeof assignment.course === 'object' && assignment.course?._id
        ? assignment.course._id
        : assignment.course;
      return assignmentCourseId === courseId;
    });
    if (courseAssignments.length === 0) return { percentage: 0, completed: 0, total: 0 };
    
    const completedCount = courseAssignments.filter(assignment => assignment.status === 'completed').length;
    const percentage = Math.round((completedCount / courseAssignments.length) * 100);
    
    return {
      percentage,
      completed: completedCount,
      total: courseAssignments.length
    };
  };

  // Filter courses by viewMode - active shows non-completed, completed shows completed
  const filteredCourses = courses.filter(course =>
    viewMode === 'active' ? course.status !== 'completed' : course.status === 'completed'
  );

  const handleEdit = (course) => {
    setEditingCourse(course);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCourse(null);
  };

  const handleFormSubmit = () => {
    setIsModalOpen(false);
    setEditingCourse(null);
  };

  const handleMarkCompleted = async (course) => {
    setCourseToComplete(course);
    setIsCompleteModalOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (!courseToComplete) return;

    try {
      await updateCourse(courseToComplete._id, { ...courseToComplete, status: 'completed' });
      setIsCompleteModalOpen(false);
      setCourseToComplete(null);
    } catch (error) {
      console.error('Failed to mark course as completed:', error);
      alert('Failed to mark course as completed. Please try again.');
    }
  };

  const handleCancelComplete = () => {
    setIsCompleteModalOpen(false);
    setCourseToComplete(null);
  };

  const handleUnmarkComplete = async (course) => {
    // Check free user course limit (5 active courses)
    const FREE_COURSE_LIMIT = 5;
    const activeCount = courses.filter(c => c.status !== 'completed').length;

    // Unmarking will increase active courses by 1
    if (!isPremium && (activeCount + 1) > FREE_COURSE_LIMIT) {
      // Prompt upgrade
      setIsSubscriptionModalOpen(true);
      alert('Unmarking this course would exceed your free course limit (5 active courses). Please upgrade to unarchive more courses.');
      return;
    }

    try {
      setIsUnmarking(true);
      await updateCourse(course._id, { ...course, status: 'active' });
    } catch (error) {
      console.error('Failed to unmark course as completed:', error);
      alert('Failed to unmark course as completed. Please try again.');
    } finally {
      setIsUnmarking(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const course = courses.find(c => c._id === courseId);
    setCourseToDelete(course);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!courseToDelete) return;
    
    try {
      await deleteCourse(courseToDelete._id);
      setIsDeleteModalOpen(false);
      setCourseToDelete(null);
      // The DataContext should automatically update the UI after deletion
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCourseToDelete(null);
  };


  const handleCloseCourseDetails = () => {
    setIsCourseDetailsModalOpen(false);
    setSelectedCourse(null);
  };

  const handleCloseCourseNotes = () => {
    setIsCourseNotesModalOpen(false);
    setSelectedCourse(null);
  };

  return (
    <>
      <Container>
        <Grid>
            {filteredCourses.map(course => (
              <Item key={course._id} color={course.color} compact={isCompact}>
                <CourseInfo>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <Link 
                        to={`/flashcards?course=${course._id}`}
                        style={{ 
                          margin: '0 0 0.25rem 0', 
                          color: 'var(--color-text)', 
                          fontSize: '1rem', 
                          fontWeight: 600,
                          textDecoration: 'underline dotted',
                          textUnderlineOffset: '4px',
                          transition: 'text-decoration 0.2s',
                          outline: 'none',
                          display: 'block',
                        }}
                        onMouseOver={e => e.currentTarget.style.textDecoration = 'underline solid'}
                        onMouseOut={e => e.currentTarget.style.textDecoration = 'underline dotted'}
                        onFocus={e => e.currentTarget.style.textDecoration = 'underline solid'}
                        onBlur={e => e.currentTarget.style.textDecoration = 'underline dotted'}
                      >
                        <CourseCode color={course.color}>{course.course_code}</CourseCode> - {course.name}
                      </Link>
                    </div>
                    {(() => {
                      if (course.grade) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                            <GradeBadge grade={course.grade}>{course.grade}</GradeBadge>
                            <div style={{ fontSize: '0.75rem', color: '#a0a0a0', fontWeight: '500' }}>
                              GPA: {letterGradeToGPA(course.grade, settings).toFixed(2)}
                            </div>
                          </div>
                        );
                      }

                      // Derive course grade/GPA from assignments when course.grade isn't set
                      const courseAssignments = (assignments || []).filter(a => {
                        const assignmentCourseId = typeof a.course === 'object' && a.course?._id
                          ? a.course._id
                          : a.course;
                        return assignmentCourseId && assignmentCourseId.toString() === course._id.toString();
                      });

                      const derivedGPA = calculateCourseGPAProjection(course, courseAssignments, settings);
                      if (derivedGPA === null || Number.isNaN(derivedGPA)) return null;

                      const derivedLetter = gpaToLetterGrade(derivedGPA, settings);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                          <GradeBadge grade={derivedLetter}>{derivedLetter}</GradeBadge>
                          <div style={{ fontSize: '0.75rem', color: '#a0a0a0', fontWeight: '500' }}>
                            GPA: {derivedGPA.toFixed(2)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  <CourseDetails>
                    {course.instructor && (
                      <div style={{ marginBottom: '0.25rem' }}>
                        👨‍🏫 {course.instructor}
                      </div>
                    )}
                    <div style={{ marginBottom: '0.25rem' }}>
                      📍 {course.location === 'online' ? 'Online' : `In-Person${course.class_number ? ` (${course.class_number})` : ''}`}
                    </div>
                    {course.description && (
                      <div style={{ marginTop: '0.5rem', color: '#b0b0b0' }}>
                        {course.description}
                      </div>
                    )}
                  </CourseDetails>

                  <ProgressSection>
                    {(() => {
                      const progress = calculateCourseProgress(course._id);
                      return (
                        <>
                          <ProgressBar
                            role="progressbar"
                            aria-valuenow={progress.percentage}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-label={`Course progress: ${progress.percentage}% complete`}
                          >
                            <ProgressFill progress={progress.percentage} />
                          </ProgressBar>
                          <ProgressText>
                            {progress.percentage}% Complete
                          </ProgressText>
                        </>
                      );
                    })()}
                  </ProgressSection>

                      <ButtonRow>
                      <EditButton onClick={() => handleEdit(course)}>Edit</EditButton>
                      {course.status !== 'completed' && (
                        <CompleteButton onClick={() => handleMarkCompleted(course)}>Complete</CompleteButton>
                      )}
                      {course.status === 'completed' && (
                        <UncompleteButton onClick={() => handleUnmarkComplete(course)} disabled={isUnmarking}>Unmark Complete</UncompleteButton>
                      )}
                      <DeleteButton onClick={() => handleDeleteCourse(course._id)}>Delete</DeleteButton>
                    </ButtonRow>
                </CourseInfo>
              </Item>
            ))}
          </Grid>
        </Container>

        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={editingCourse ? 'Edit Course' : 'Add Course'}
        >
          <CourseForm
            course={editingCourse}
            onSubmit={handleFormSubmit}
            isEditing={!!editingCourse}
          />
        </Modal>

        <SubscriptionModal
          isOpen={isSubscriptionModalOpen}
          onClose={() => setIsSubscriptionModalOpen(false)}
        />

        {/* All Assignments Modal */}
        <Modal
          isOpen={isAllAssignmentsModalOpen}
          onClose={() => setIsAllAssignmentsModalOpen(false)}
          title="All Assignments"
        >
          <div style={{ height: '70vh', overflow: 'hidden' }}>
            <AssignmentList />
          </div>
        </Modal>

        {/* Course Delete Confirmation Modal */}
        <CourseDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={handleCancelDelete}
          courseName={courseToDelete?.name || ''}
          onConfirm={handleConfirmDelete}
        />

        {/* Course Complete Confirmation Modal */}
        <GenericConfirmModal
          isOpen={isCompleteModalOpen}
          onClose={handleCancelComplete}
          title="Mark Course as Completed"
          message={`Are you sure you want to mark "${courseToComplete?.name || ''}" as completed?`}
          confirmText="Mark as Completed"
          confirmButtonColor="#28a745"
          onConfirm={handleConfirmComplete}
        />

        {/* Course Details Modal */}
        <CourseDetailsModal
          isOpen={isCourseDetailsModalOpen}
          onClose={handleCloseCourseDetails}
          course={selectedCourse}
        />

        {/* Course Notes Modal */}
        <CourseNotesModal
          isOpen={isCourseNotesModalOpen}
          onClose={handleCloseCourseNotes}
          course={selectedCourse}
        />
      </>
    );
};

export default CourseList;